import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, Shield, TrendingUp, Users, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { financialHighlights, governanceDocs, teamMembers, faqs } from '../data/mock';

const Transparency = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Transparency & Governance</h1>
          <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
            Accountability isn't optional—it's our responsibility. See exactly how we steward your trust.
          </p>
        </div>
      </section>

      {/* Financial Highlights */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Financial Overview</h2>
            <p className="text-xl text-gray-600">Financial Year 2023/24</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {financialHighlights.map((item, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#2D5F3F] transition-all duration-300 shadow-md">
                <CardContent className="p-8 text-center">
                  <TrendingUp className="w-12 h-12 text-[#2D5F3F] mx-auto mb-4" />
                  <div className="text-3xl font-bold text-[#2D5F3F] mb-2">{item.value}</div>
                  <div className="text-sm text-gray-600">{item.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="border-l-8 border-[#D4AF37] shadow-lg">
            <CardContent className="p-8">
              <div className="flex items-start space-x-4">
                <div className="bg-[#D4AF37]/10 p-3 rounded-xl">
                  <Eye className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#2D5F3F] mb-3">Where Your Money Goes</h3>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">
                    We're proud to maintain one of the lowest overhead ratios in the charity sector. 88 pence of every pound goes directly to collecting, refurbishing, shipping, and distributing life-changing equipment.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-3 mr-4">
                        <div className="bg-gradient-to-r from-[#2D5F3F] to-[#3A7D4A] h-3 rounded-full" style={{width: '88%'}}></div>
                      </div>
                      <span className="text-sm font-semibold text-[#2D5F3F] whitespace-nowrap">88% Programs</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-3 mr-4">
                        <div className="bg-[#D4AF37] h-3 rounded-full" style={{width: '8%'}}></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">8% Admin</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-3 mr-4">
                        <div className="bg-gray-400 h-3 rounded-full" style={{width: '4%'}}></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">4% Fundraising</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Governance Structure */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Governance & Leadership</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: Shield,
                title: "Independent Board",
                description: "5 trustees with diverse expertise providing strategic oversight and ensuring accountability"
              },
              {
                icon: FileText,
                title: "Regular Audits",
                description: "Annual independent audit and quarterly financial reviews by our board's finance committee"
              },
              {
                icon: Users,
                title: "Charity Commission",
                description: "Registered UK charity (No. 1234567) in full compliance with regulatory requirements"
              }
            ].map((item, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#2D5F3F] to-[#3A7D4A] rounded-2xl mb-6">
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2D5F3F] mb-3">{item.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-[#2D5F3F] shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#2D5F3F] mb-4">Our Trustees</h3>
                <p className="text-gray-700 mb-6">
                  Our board brings together expertise in charity governance, finance, healthcare, logistics, and international development.
                </p>
                <ul className="space-y-3">
                  {teamMembers.slice(0, 3).map((member) => (
                    <li key={member.id} className="flex items-center space-x-3">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-semibold text-[#2D5F3F]">{member.name}</div>
                        <div className="text-sm text-gray-600">{member.role}</div>
                      </div>
                    </li>
                  ))}
                </ul>
                <Link to="/about#team" className="inline-block mt-6">
                  <Button variant="outline" className="border-[#2D5F3F] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-white">
                    View Full Team
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#D4AF37] shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-[#2D5F3F] mb-4">Policies & Safeguards</h3>
                <ul className="space-y-4">
                  {[
                    "Safeguarding Policy (DBS checks for all volunteers)",
                    "Conflict of Interest Policy",
                    "Financial Reserves Policy (6 months operating costs)",
                    "Data Protection & Privacy (GDPR compliant)",
                    "Complaints & Whistleblowing Procedures",
                    "Risk Management Framework"
                  ].map((policy, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{policy}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Documents & Reports */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Reports & Documents</h2>
            <p className="text-xl text-gray-600">
              Download our latest reports, accounts, and governance documents
            </p>
          </div>

          <div className="space-y-4">
            {governanceDocs.map((doc, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-[#2D5F3F] transition-all duration-300 hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="bg-[#2D5F3F]/10 p-3 rounded-xl">
                        <FileText className="w-6 h-6 text-[#2D5F3F]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#2D5F3F]">{doc.title}</h3>
                        <p className="text-sm text-gray-600">{doc.type} • {doc.size}</p>
                      </div>
                    </div>
                    <Button variant="outline" className="border-[#2D5F3F] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-white">
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="mt-8 bg-gradient-to-br from-[#2D5F3F]/5 to-[#D4AF37]/5 border-2 border-[#D4AF37]">
            <CardContent className="p-8 text-center">
              <Eye className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[#2D5F3F] mb-3">Looking for something else?</h3>
              <p className="text-gray-700 mb-6">
                We're committed to full transparency. If you need additional information or documents, please contact us.
              </p>
              <Link to="/contact">
                <Button className="bg-[#D4AF37] hover:bg-[#C19B2C] text-white">
                  Contact Us
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2D5F3F] mb-4">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border-2 border-gray-100 rounded-xl px-6">
                <AccordionTrigger className="text-left font-semibold text-[#2D5F3F] hover:text-[#3A7D4A]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default Transparency;
